import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaArrowLeft, FaDownload, FaGithub } from 'react-icons/fa';

function GameDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  // Sample game data - Cat Watching Game
  const gameData = {
    'cat-watching-game': {
      title: 'Cat Watching Game',
      emoji: '🐱',
      shortDesc: 'Motion detection game where a cat watches you play!',
      fullDesc: 'This is an interactive game built for Raspberry Pi where a virtual cat watches your movements using a camera and motion detection. The cat reacts to your movements - blink and the game ends!',

      components: [
        { name: 'Raspberry Pi 3', icon: '🥧', desc: 'Main processor' },
        { name: 'Camera Module', icon: '📷', desc: 'Motion detection' },
        { name: 'LCD Screen', icon: '🖥️', desc: '7" Touch display' },
        { name: 'Speaker', icon: '🔊', desc: 'Sound effects' },
      ],

      howToPlay: [
        'Power on the game on the Raspberry Pi',
        'Look at the screen and the cat will look at you',
        'Try not to blink - the cat will catch you!',
        'If you blink, the game ends and your score appears',
        'Play again to beat your high score!',
      ],

      gameplay: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
      youtubeChannel: 'https://youtube.com/@aigaminggirl',

      technologies: [
        'Python',
        'OpenCV',
        'TensorFlow',
        'Raspberry Pi OS',
      ],

      stats: {
        difficulty: 'Easy',
        ageGroup: '5-15 years',
        playTime: '2-5 minutes',
        players: '1 Player',
      },
    },
  };

  const game = gameData[id] || gameData['cat-watching-game'];

  return (
    <div className="w-full min-h-screen bg-gradient-to-br from-purple-600 via-purple-700 to-purple-800">
      {/* Back Button */}
      <motion.button
        whileHover={{ x: -5 }}
        onClick={() => navigate('/my-games')}
        className="fixed left-4 md:left-8 top-24 z-40 flex items-center gap-2 bg-yellow-300 text-purple-900 font-black px-5 py-3 rounded-xl shadow-lg hover:shadow-xl border-3 border-yellow-400"
      >
        <FaArrowLeft /> Back
      </motion.button>

      {/* Hero Section */}
      <section className="py-12 px-4 bg-gradient-to-b from-purple-600 to-purple-700 text-white">
        <motion.div
          className="container mx-auto text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="text-8xl mb-4">{game.emoji}</div>
          <h1 className="text-5xl md:text-6xl font-black mb-4 font-heading">
            {game.title}
          </h1>
          <p className="text-xl max-w-2xl mx-auto">
            {game.shortDesc}
          </p>
        </motion.div>
      </section>

      {/* Main Content */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-5xl">
          {/* About Game */}
          <motion.div
            className="mb-12 bg-white p-8 md:p-12 rounded-3xl shadow-cartoon-lg border-4 border-yellow-300"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-black text-primary mb-6 font-heading">
              About This Game 📖
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed">
              {game.fullDesc}
            </p>
          </motion.div>

          {/* Video Section */}
          <motion.div
            className="mb-12 bg-white p-8 md:p-12 rounded-3xl shadow-cartoon-lg border-4 border-red-500"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-black text-primary mb-6 font-heading">
              Watch Me Play! 🎥
            </h2>
            <div className="aspect-video bg-gray-900 rounded-2xl overflow-hidden">
              <iframe
                width="100%"
                height="100%"
                src={game.gameplay}
                title={game.title}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          </motion.div>

          {/* Components */}
          <motion.div
            className="mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-black text-primary mb-8 font-heading">
              Components Used 🔧
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {game.components.map((comp, index) => (
                <motion.div
                  key={index}
                  whileHover={{ y: -8 }}
                  className="bg-white p-6 rounded-2xl shadow-cartoon border-4 border-purple-500 hover:border-yellow-300 transition-all"
                >
                  <div className="text-5xl mb-3">{comp.icon}</div>
                  <h4 className="text-xl font-bold text-primary mb-2 font-heading">
                    {comp.name}
                  </h4>
                  <p className="text-gray-600">{comp.desc}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* How to Play */}
          <motion.div
            className="mb-12 bg-white p-8 md:p-12 rounded-3xl shadow-lg"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-black text-primary mb-6 font-heading">
              How to Play 🎮
            </h2>
            <ol className="space-y-4">
              {game.howToPlay.map((step, index) => (
                <motion.li
                  key={index}
                  whileHover={{ x: 10 }}
                  className="flex gap-4 text-lg text-gray-700 bg-gradient-to-r from-purple-50 to-yellow-50 p-4 rounded-xl"
                >
                  <span className="font-bold text-primary text-2xl min-w-10">
                    {index + 1}
                  </span>
                  <span>{step}</span>
                </motion.li>
              ))}
            </ol>
          </motion.div>

          {/* Game Stats */}
          <motion.div
            className="mb-12 grid grid-cols-2 md:grid-cols-4 gap-4"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            {Object.entries(game.stats).map(([key, value]) => (
              <motion.div
                key={key}
                whileHover={{ y: -5 }}
                className="bg-white p-6 rounded-2xl shadow-lg text-center"
              >
                <p className="text-gray-600 capitalize text-sm font-semibold mb-2">
                  {key.replace(/([A-Z])/g, ' $1')}
                </p>
                <p className="text-2xl font-black text-primary font-heading">
                  {value}
                </p>
              </motion.div>
            ))}
          </motion.div>

          {/* Technologies */}
          <motion.div
            className="mb-12 bg-white p-8 md:p-12 rounded-3xl shadow-lg"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-black text-primary mb-6 font-heading">
              Built With 💻
            </h2>
            <div className="flex flex-wrap gap-3">
              {game.technologies.map((tech, index) => (
                <motion.span
                  key={index}
                  whileHover={{ scale: 1.1 }}
                  className="px-6 py-3 bg-gradient-to-r from-primary to-primaryLight text-white font-bold rounded-full"
                >
                  {tech}
                </motion.span>
              ))}
            </div>
          </motion.div>

          {/* Download & Links */}
          <motion.div
            className="flex flex-col md:flex-row gap-4"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <motion.a
              href="#download"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="flex-1 bg-gradient-to-r from-primary to-primaryLight text-white font-bold py-4 px-6 rounded-2xl flex items-center justify-center gap-2 hover:shadow-lg transition-shadow"
            >
              <FaDownload /> Download Code
            </motion.a>
            <motion.a
              href="https://github.com"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="flex-1 bg-white border-2 border-primary text-primary font-bold py-4 px-6 rounded-2xl flex items-center justify-center gap-2 hover:shadow-lg transition-shadow"
            >
              <FaGithub /> View on GitHub
            </motion.a>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default GameDetail;
